import type { SettingsPatch, SettingsResponse } from "@plenum/shared/api";
import { useEffect, useState } from "react";
import { ApiError, usePatchSettings, useSettings } from "../lib/api";
import { fmtDate } from "../lib/format";
import { FRAME_LABEL } from "../lib/tiers";
import { Pill } from "../ui/Badge";
import { useToast } from "../ui/Toast";

/**
 * Settings (PRD §12.1): cooling parameters, scoring weights, price assumptions, budgets, API key
 * status and org members. Defaults come from config/*.json; edits are per-org overrides stored on
 * the org (PRD §14.5) and drive on-demand estimates and the UI. Pipeline runs use the global files.
 */
export function SettingsPage() {
  const q = useSettings();
  const patch = usePatchSettings();
  const toast = useToast();
  const [draft, setDraft] = useState<SettingsPatch>({});
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (q.data && !dirty) setDraft(structuredClone(q.data.overrides) as SettingsPatch);
  }, [q.data, dirty]);

  if (q.isLoading)
    return (
      <div className="p-6" style={{ color: "var(--muted)" }}>
        Loading…
      </div>
    );
  if (q.error || !q.data) {
    return (
      <div className="p-6" style={{ color: "var(--danger)" }}>
        {q.error instanceof ApiError ? q.error.message : "Could not load settings"}
      </div>
    );
  }
  const d = q.data;
  const eff = d.effective;
  const cooling = eff.cooling as Record<string, unknown>;
  const scoring = eff.scoring as Record<string, unknown>;
  const budgets = eff.budgets as Record<string, unknown>;

  const setNum = (path: string[], value: string) => {
    setDirty(true);
    setDraft((prev) => {
      const next = structuredClone(prev) as Record<string, unknown>;
      let cur: Record<string, unknown> = next;
      for (const k of path.slice(0, -1)) {
        cur[k] = (cur[k] as Record<string, unknown> | undefined) ?? {};
        cur = cur[k] as Record<string, unknown>;
      }
      const last = path[path.length - 1] as string;
      if (value === "") delete cur[last];
      else cur[last] = Number(value);
      return next as SettingsPatch;
    });
  };
  const overrideAt = (path: string[]): number | undefined => {
    let cur: unknown = draft;
    for (const k of path) {
      if (!cur || typeof cur !== "object") return undefined;
      cur = (cur as Record<string, unknown>)[k];
    }
    return typeof cur === "number" ? cur : undefined;
  };
  const defaultAt = (path: string[]): unknown => {
    let cur: unknown = d.defaults;
    for (const k of path) {
      if (!cur || typeof cur !== "object") return undefined;
      cur = (cur as Record<string, unknown>)[k];
    }
    return cur;
  };

  async function save() {
    try {
      await patch.mutateAsync(draft);
      setDirty(false);
      toast("Settings saved as org overrides");
    } catch (e) {
      toast(
        e instanceof ApiError
          ? `${e.message}${e.details ? `: ${JSON.stringify(e.details)}` : ""}`
          : "Save failed",
        "danger",
      );
    }
  }
  async function reset() {
    try {
      await patch.mutateAsync({});
      setDraft({});
      setDirty(false);
      toast("Overrides cleared");
    } catch (e) {
      toast(e instanceof ApiError ? e.message : "Reset failed", "danger");
    }
  }

  const Field = ({ path, step = 0.01 }: { path: string[]; step?: number }) => {
    const def = defaultAt(path);
    const ov = overrideAt(path);
    return (
      <span className="flex items-center gap-1">
        <input
          className="input num w-24"
          type="number"
          step={step}
          value={ov ?? ""}
          placeholder={typeof def === "number" ? String(def) : ""}
          aria-label={path.join(".")}
          onChange={(e) => setNum(path, e.target.value)}
        />
        {ov !== undefined && ov !== def ? <Pill tone="gold">override</Pill> : null}
      </span>
    );
  };

  const frameClasses = Object.keys(cooling.sens_pct_per_c as Record<string, number>);
  const weights = scoring.weights as Record<string, number>;
  const weightSum = Object.keys(weights).reduce(
    (a, k) => a + (overrideAt(["scoring", "weights", k]) ?? weights[k] ?? 0),
    0,
  );

  return (
    <div className="scroll-thin mx-auto h-full max-w-5xl overflow-auto p-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-[18px] font-semibold">Settings</h1>
          <p className="text-[12px]" style={{ color: "var(--muted)" }}>
            Org <span className="num">{d.org.slug}</span> · plan {d.org.plan}. Empty fields use the
            repo defaults; a value becomes a per-org override.
            {d.updated_at ? ` Last saved ${fmtDate(d.updated_at)} by ${d.updated_by}.` : ""}
          </p>
        </div>
        <div className="flex gap-2">
          <button type="button" className="btn btn-sm" onClick={reset} disabled={patch.isPending}>
            Clear overrides
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={save}
            disabled={patch.isPending || !dirty}
            data-testid="settings-save"
          >
            {patch.isPending ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <section className="mt-6">
        <h2 className="eyebrow mb-2">Cooling model · {String(cooling.model_version)}</h2>
        <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-[12px] md:grid-cols-4">
          <Label text="T_on °C">
            <Field path={["cooling", "T_on_c"]} step={0.5} />
          </Label>
          <Label text="dx_min g/kg">
            <Field path={["cooling", "dx_min_g_kg"]} step={0.1} />
          </Label>
          <Label text="epsilon">
            <Field path={["cooling", "epsilon"]} step={0.01} />
          </Label>
          <Label text="dT_max °C">
            <Field path={["cooling", "dT_max_c"]} step={0.5} />
          </Label>
          <Label text="drain fraction">
            <Field path={["cooling", "drain_fraction"]} step={0.005} />
          </Label>
        </div>
        <table className="tbl mt-3 max-w-2xl">
          <thead>
            <tr>
              <th>Frame class</th>
              <th className="r">Sensitivity % per °C</th>
              <th className="r">Air flow kg/s per MW</th>
            </tr>
          </thead>
          <tbody>
            {frameClasses.map((fc) => (
              <tr key={fc}>
                <td>{FRAME_LABEL[fc] ?? fc}</td>
                <td className="r">
                  <Field path={["cooling", "sens_pct_per_c", fc]} step={0.05} />
                </td>
                <td className="r">
                  <Field path={["cooling", "air_flow_kg_s_per_mw", fc]} step={0.1} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <h3 className="eyebrow mt-4 mb-1">
          Indicative price, USD per MWh (summer on-peak, disclaimer shown with every value)
        </h3>
        <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-[12px] md:grid-cols-4">
          {Object.keys(cooling.price_usd_per_mwh as Record<string, number>).map((ba) => (
            <Label key={ba} text={ba}>
              <Field path={["cooling", "price_usd_per_mwh", ba]} step={1} />
            </Label>
          ))}
        </div>
      </section>

      <section className="mt-6">
        <h2 className="eyebrow mb-2">
          Prospect score · {String(scoring.model_version)} · weights sum{" "}
          <span
            className="num"
            style={{ color: Math.abs(weightSum - 1) > 0.001 ? "var(--danger)" : "var(--ok)" }}
          >
            {weightSum.toFixed(3)}
          </span>
        </h2>
        <table className="tbl max-w-2xl">
          <thead>
            <tr>
              <th>Component</th>
              <th className="r">Weight</th>
            </tr>
          </thead>
          <tbody>
            {Object.keys(weights).map((k) => (
              <tr key={k}>
                <td>{k.replace(/_/g, " ")}</td>
                <td className="r">
                  <Field path={["scoring", "weights", k]} step={0.05} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-3 grid grid-cols-2 gap-x-8 gap-y-2 text-[12px] md:grid-cols-4">
          <Label text="Tier 1 ≥">
            <Field path={["scoring", "tiers", "1"]} step={1} />
          </Label>
          <Label text="Tier 2 ≥">
            <Field path={["scoring", "tiers", "2"]} step={1} />
          </Label>
          <Label text="Tier 3 ≥">
            <Field path={["scoring", "tiers", "3"]} step={1} />
          </Label>
          <Label text="Site bonus per extra unit">
            <Field path={["scoring", "site_bonus_per_extra_unit"]} step={1} />
          </Label>
          <Label text="Age cutoff year">
            <Field path={["scoring", "age_cutoff_year"]} step={1} />
          </Label>
        </div>
        <p className="mt-2 text-[11px]" style={{ color: "var(--muted-2)" }}>
          Changing weights in the repo bumps model_version and re-scores in the pipeline (PRD §9).
          Overrides here apply to on-demand estimates and previews for this org only.
        </p>
      </section>

      <section className="mt-6">
        <h2 className="eyebrow mb-2">Band and budgets</h2>
        <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-[12px] md:grid-cols-4">
          <Label text="Default band min MW">
            <Field path={["band", "mw_min"]} step={5} />
          </Label>
          <Label text="Default band max MW">
            <Field path={["band", "mw_max"]} step={5} />
          </Label>
          <Label text="Per site USD">
            <Field path={["budgets", "per_site_usd"]} step={0.5} />
          </Label>
          <Label text="Per run USD">
            <Field path={["budgets", "per_run_usd"]} step={10} />
          </Label>
          <Label text="Monthly USD">
            <Field path={["budgets", "monthly_usd"]} step={100} />
          </Label>
        </div>
        <p className="mt-2 text-[11px]" style={{ color: "var(--muted-2)" }}>
          Repo defaults: per site {String(budgets.per_site_usd)}, per run{" "}
          {String(budgets.per_run_usd)}, monthly {String(budgets.monthly_usd)} USD
          (config/budgets.json, Protected Path). Paid APIs: Hunter{" "}
          {(budgets.paid_apis as Record<string, { enabled: boolean }>).hunter?.enabled
            ? "on"
            : "off"}
          , Apollo{" "}
          {(budgets.paid_apis as Record<string, { enabled: boolean }>).apollo?.enabled
            ? "on"
            : "off"}
          .
        </p>
      </section>

      <section className="mt-6 grid gap-6 md:grid-cols-2">
        <div>
          <h2 className="eyebrow mb-2">API keys (presence only)</h2>
          <ul className="flex flex-col gap-1 text-[12px]">
            {Object.entries(d.secrets).map(([k, v]) => (
              <li key={k} className="flex items-center gap-2">
                <span className="num">{k}</span>
                <Pill tone={v ? "ok" : "muted"}>{v ? "set" : "missing"}</Pill>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h2 className="eyebrow mb-2">Members</h2>
          <table className="tbl">
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
              </tr>
            </thead>
            <tbody>
              {d.members.map((m) => (
                <tr key={m.email}>
                  <td>{m.email}</td>
                  <td>{m.role}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-2 text-[11px]" style={{ color: "var(--muted-2)" }}>
            Membership is managed through Cloudflare Access and the memberships table; self-service
            invites arrive with v1.1 auth (PRD §14.4).
          </p>
        </div>
      </section>
    </div>
  );
}

function Label({ text, children }: { text: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span style={{ color: "var(--muted)" }}>{text}</span>
      {children}
    </div>
  );
}

export type { SettingsResponse };
